import React, { useState, useRef, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Autocomplete,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ArrowForward } from '@mui/icons-material';
import { useInventory } from '../../../contexts/InventoryContext';
import InventoryKeyHighlighter from '../../../components/inventory/InventoryKeyHighlighter';
import { SectionHeader, SECTION_COLORS } from '../../../utils/inventory_helper';
const DRAWER_WIDTH = 440;


const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: 12.5,
    height: 32,
    borderRadius: '6px',
    '& fieldset': { borderColor: SECTION_COLORS.border },
    '&:hover fieldset': { borderColor: '#B0B8C4' },
    '&.Mui-focused fieldset': { borderColor: '#2D6A4F', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontSize: 12, top: -2 },
  '& .MuiInputLabel-shrink': { top: 0 },
};
const fieldSxTall = {
  '& .MuiOutlinedInput-root': {
    fontSize: 13,
    borderRadius: '6px',
    '& fieldset': { borderColor: SECTION_COLORS.border },
    '&:hover fieldset': { borderColor: '#B0B8C4' },
    '&.Mui-focused fieldset': { borderColor: '#2D6A4F', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontSize: 12.5 },
  '& .MuiInputBase-input::placeholder': { fontSize: 12, opacity: 0.55 },
};

const FieldLabel = ({ label, required }) => (
  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: SECTION_COLORS.text, lineHeight: 1, mb: 0.75 }}>
    {label}
    {required && <span style={{ color: SECTION_COLORS.clearAllRed, marginLeft: 2 }}>*</span>}
  </Typography>
);

const AddNewDeveloper = ({ open, onClose, initialData = null }) => {
  const { createDeveloper, updateDeveloper, refetchOptions, inventoryOptions } = useInventory();
  const fileInputRef = useRef(null);
  const projectTagSuggestions = inventoryOptions?.project_tag_suggestions ?? [];
  const isEditMode = Boolean(initialData?.id);

  const [name, setName] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [website, setWebsite] = useState('');
  const [tags, setTags] = useState([]);
  const [keyHighlights, setKeyHighlights] = useState(['']);
  const [nameError, setNameError] = useState('');
  const [logoError, setLogoError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && initialData) {
      setName(initialData.name || '');
      setWebsite(initialData.website || '');
      setTags(Array.isArray(initialData.tags) ? initialData.tags : []);
      setKeyHighlights(
        Array.isArray(initialData.key_highlights) && initialData.key_highlights.length > 0
          ? initialData.key_highlights
          : ['']
      );
      setLogoFile(null);
      setLogoPreview(initialData.logo || '');
    }
  }, [open, initialData]);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setLogoError('');
    }
  };

  const handleSubmit = async () => {
    setNameError('');
    setLogoError('');
    if (!name.trim()) {
      setNameError('Developer name is required');
      return;
    }
    setSubmitting(true);
    try {
      if (isEditMode) {
        if (logoFile) {
          const formData = new FormData();
          formData.append('name', name.trim());
          formData.append('website', website.trim() || '');
          formData.append('logo', logoFile);
          const tagsList = (tags || []).map((t) => String(t).trim()).filter(Boolean);
          formData.append('tags', JSON.stringify(tagsList));
          const highlightsList = keyHighlights.map((h) => h.trim()).filter(Boolean);
          formData.append('key_highlights', JSON.stringify(highlightsList));
          await updateDeveloper(initialData.id, formData);
        } else {
          const payload = {
            name: name.trim(),
            website: website.trim() || null,
            tags: (tags || []).map((t) => String(t).trim()).filter(Boolean),
            key_highlights: keyHighlights.map((h) => h.trim()).filter(Boolean),
          };
          await updateDeveloper(initialData.id, payload);
        }
        refetchOptions();
      } else {
        const formData = new FormData();
        formData.append('name', name.trim());
        formData.append('logo', logoFile);
        if (website.trim()) formData.append('website', website.trim());
        const tagsList = (tags || []).map((t) => String(t).trim()).filter(Boolean);
        if (tagsList.length) formData.append('tags', JSON.stringify(tagsList));
        const highlightsList = keyHighlights.map((h) => h.trim()).filter(Boolean);
        if (highlightsList.length)
          formData.append('key_highlights', JSON.stringify(highlightsList));
        await createDeveloper(formData);
      }
      handleClose();
    } catch (err) {
      const msg = err?.response?.data?.name?.[0] || err?.response?.data?.logo?.[0] || err?.message || 'Failed to save developer';
      if (err?.response?.data?.logo?.[0]) setLogoError(msg);
      else setNameError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setName('');
    setLogoFile(null);
    if (logoPreview && logoPreview.startsWith('blob:')) URL.revokeObjectURL(logoPreview);
    setLogoPreview('');
    setWebsite('');
    setTags([]);
    setKeyHighlights(['']);
    setNameError('');
    setLogoError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: DRAWER_WIDTH },
          maxWidth: '100%',
          boxShadow: '-8px 0 40px rgba(0,0,0,0.14)',
          bgcolor: '#F0F2F5',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${SECTION_COLORS.border}`, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
              <Box sx={{ width: 4, height: 20, bgcolor: SECTION_COLORS.green, borderRadius: 1 }} />
              <Typography fontWeight={800} sx={{ color: SECTION_COLORS.text, letterSpacing: '-0.3px', fontSize: '1.05rem' }}>
                {isEditMode ? 'Edit Developer' : 'Add Developer'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 11.5, color: SECTION_COLORS.sub, pl: 1.5 }}>
              {isEditMode ? 'Update developer details' : 'Create a developer profile to link to projects'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ color: SECTION_COLORS.sub }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Form */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: 2.5, py: 2 }}>
          <Box sx={{
            bgcolor: '#fff',
            borderRadius: '10px',
            border: `1px solid ${SECTION_COLORS.border}`,
            boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
            mb: 2,
          }}>
            <Box sx={{ borderLeft: `3px solid ${SECTION_COLORS.green}`, px: 2, pt: 1.75, pb: 0.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: SECTION_COLORS.green, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Developer Details
              </Typography>
            </Box>
            <Box sx={{ borderLeft: `3px solid ${SECTION_COLORS.green}`, px: 2, pb: 2, pt: 0.5 }}>
              <Box sx={{ mb: 2 }}>
                <FieldLabel label="Developer Name" required />
                <TextField
                  placeholder="e.g. Oberoi Realty"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setNameError(''); }}
                  error={Boolean(nameError)}
                  helperText={nameError}
                  fullWidth
                  variant="outlined"
                  size="small"
                  sx={fieldSxTall}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <FieldLabel label="Developer Logo" />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  style={{ display: 'none' }}
                />
                <Box
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files?.[0];
                    if (file && file.type.startsWith('image/')) {
                      setLogoFile(file);
                      setLogoPreview(URL.createObjectURL(file));
                      setLogoError('');
                    }
                  }}
                  sx={{
                    borderRadius: '6px',
                    border: `1px solid ${SECTION_COLORS.border}`,
                    bgcolor: '#fff',
                    py: 2,
                    px: 1.5,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { borderColor: '#B0B8C4', bgcolor: '#FAFAFA' },
                  }}
                >
                  {logoPreview ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <Box
                        component="img"
                        src={logoPreview}
                        alt="Logo preview"
                        sx={{ maxHeight: 48, maxWidth: '100%', objectFit: 'contain' }}
                      />
                      <Typography sx={{ fontSize: 11.5, color: SECTION_COLORS.sub }}>
                        {logoFile?.name || 'Click or drop to change'}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: 12, color: SECTION_COLORS.sub }}>Upload Logo</Typography>
                  )}
                </Box>
                {logoError && (
                  <Typography sx={{ fontSize: 11, color: 'error.main', mt: 0.5, display: 'block' }}>
                    {logoError}
                  </Typography>
                )}
              </Box>

              <Box sx={{ mb: 2 }}>
                <FieldLabel label="Website (optional)" />
                <TextField
                  placeholder="https://example.com"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  sx={fieldSx}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <FieldLabel label="Tags (optional)" />
                <Autocomplete
                  multiple
                  freeSolo
                  options={projectTagSuggestions}
                  value={tags || []}
                  onChange={(_, v) => setTags(v || [])}
                  getOptionLabel={(o) => (typeof o === 'string' ? o : o?.label ?? '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Add tags (e.g. Luxury, Residential, Mumbai)"
                      size="small"
                      sx={{
                        ...fieldSx,
                        '& .MuiOutlinedInput-root': { height: 'auto', minHeight: 32 },
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option}
                        label={option}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          bgcolor: `${SECTION_COLORS.green}14`,
                          color: SECTION_COLORS.green,
                          border: `1px solid ${SECTION_COLORS.green}40`,
                          '& .MuiChip-deleteIcon': { fontSize: 13 },
                        }}
                      />
                    ))
                  }
                />
              </Box>
               {/* Key highlighter */}
               <Box>
                <SectionHeader 
                   title="Key highlighter"
                   subtitle="Optional highlights for this developer"
                   accent="highlights" 
                  />
                <InventoryKeyHighlighter 
                  value={keyHighlights} 
                  onChange={setKeyHighlights} 
                  placeholder="e.g. 25+ years experience" 
                  accent="highlights" 
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ px: 2.5, py: 2, borderTop: `1px solid ${SECTION_COLORS.border}`, flexShrink: 0, bgcolor: '#F0F2F5' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button
              onClick={handleClose}
              size="small"
              sx={{
                fontSize: 12.5,
                fontWeight: 600,
                textTransform: 'none',
                bgcolor: '#e8f5e9',
                color: '#1B4332',
                borderRadius: '6px',
                px: 2,
                '&:hover': { bgcolor: '#c8e6c9' },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
            endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
            onClick={handleSubmit}
              disabled={submitting}
              sx={{
                fontSize: 12.5,
                fontWeight: 600,
                textTransform: 'none',
                bgcolor: '#1B4332',
                borderRadius: '6px',
                boxShadow: 'none',
                px: 2,
                '&:hover': { bgcolor: '#163d2b' },
              }}
            >
              {isEditMode ? 'Update Developer' : 'Create Developer'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AddNewDeveloper;
